import { useMutation } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { toast } from '../components/ui/toast.js';
import { SUPPRESSIONS_EXPORT_TOO_MANY_ROWS_ERROR_CODE } from '../constants/suppressions.js';
import { getApiErrorCode } from '../lib/error.js';
import { useIntl } from './useIntl.js';

export type SuppressionsExportVariables = {
  deviceIds: string[];
  from: string;
  to?: string;
};

type SuppressionsExportCount = {
  rows: number;
  maxRows: number;
  exceedsCap: boolean;
};

const toLocalDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

const buildFallbackFilename = (from: string, to: string | undefined) => {
  const end = to ? new Date(Math.min(new Date(to).getTime(), Date.now())) : new Date();
  return `suppressions_${toLocalDate(new Date(from))}_${toLocalDate(end)}.csv`;
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const useSuppressionsExport = () => {
  const { locale, formatMessage } = useIntl();

  return useMutation({
    mutationFn: async ({ deviceIds, from, to }: SuppressionsExportVariables) => {
      const params = {
        deviceIds: deviceIds.join(','),
        from: new Date(from).toISOString(),
        ...(to ? { to: new Date(to).toISOString() } : {}),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale,
      };

      const { data: count } = await apiClient.get<SuppressionsExportCount>(
        '/analytics/suppressions/export/count',
        { params },
      );
      if (count.exceedsCap) {
        return count;
      }

      const response = await apiClient.get<Blob>('/analytics/suppressions/export', {
        params,
        responseType: 'blob',
      });
      const filename =
        /filename="([^"]+)"/.exec(response.headers['content-disposition'] ?? '')?.[1] ??
        buildFallbackFilename(from, to);
      saveBlob(response.data, filename);
      return count;
    },
    onSuccess: (count) => {
      if (count.exceedsCap) {
        toast.error(
          formatMessage(
            { id: 'analytics.suppressionsReport.tooManyRows' },
            { rows: count.rows, maxRows: count.maxRows },
          ),
        );
      }
    },
    onError: (error) => {
      // The export re-counts over its own window, so rows added since the count step can still
      // trip the cap; its 400 carries no row numbers.
      const id =
        getApiErrorCode(error) === SUPPRESSIONS_EXPORT_TOO_MANY_ROWS_ERROR_CODE
          ? 'analytics.suppressionsReport.tooManyRowsAtExport'
          : 'analytics.suppressionsReport.failed';
      toast.error(formatMessage({ id }));
    },
  });
};
